/**
 * use-message-in-sight.ts — a form's message above its actions, kept in sight
 * as it comes.
 *
 * A long form's body scrolls, so a message at its top is out of sight while
 * the person is at Save. Placed above the actions instead, it can arrive at
 * the body's lower edge and push them below it. So as a message arrives, this
 * brings the actions into sight, with the message just above them, and gives
 * the message the focus, where the person is.
 *
 * Usage, with the message in a focusable holder right above the actions:
 *
 *     const { messageRef, actionsRef } = useMessageInSight(error);
 *     …
 *     {error && <div ref={messageRef} tabIndex={-1}><FormMessage …/></div>}
 *     <div ref={actionsRef} className={…}>…Save…</div>
 */

import { useEffect, useRef } from 'react';

/** The holders of a form's message and its actions; each new message brings both into sight and takes the focus. */
export function useMessageInSight(message: unknown) {
  const messageRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!message) return;
    actionsRef.current?.scrollIntoView({ block: 'nearest' });
    messageRef.current?.focus({ preventScroll: true });
  }, [message]);
  return { messageRef, actionsRef };
}
